// Smaller Insights cards: reward funnel, staff, cards, coming up.
import { Link } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { initialsFrom } from "../../../utils/initials";
import { formatBirthday, formatNumber, formatPercent } from "../format";
import { EmptyNote, Panel, Skeleton } from "./ui";

const FUNNEL_STEPS = ["signed_up", "first_visit", "three_plus_visits", "first_reward"];

export function RewardFunnel({ funnel, loading }) {
  const { t, language } = useLanguage();
  const top = funnel?.signed_up || 0;

  // The step that loses the largest share of the one before it.
  let drop = null;
  for (let i = 1; i < FUNNEL_STEPS.length; i += 1) {
    const before = funnel?.[FUNNEL_STEPS[i - 1]] || 0;
    const after = funnel?.[FUNNEL_STEPS[i]] || 0;
    if (before > 0) {
      const lost = (before - after) / before;
      if (!drop || lost > drop.lost) drop = { lost, from: FUNNEL_STEPS[i - 1], to: FUNNEL_STEPS[i] };
    }
  }

  return (
    <Panel
      title={t("dashboard.insights.funnel.title")}
      subtitle={t("dashboard.insights.funnel.subtitle")}
      className="flex-[1_1_300px]"
    >
      {loading ? (
        <div className="flex flex-col gap-4">
          {FUNNEL_STEPS.map((s) => (
            <Skeleton key={s} className="h-12 w-full" />
          ))}
        </div>
      ) : top === 0 ? (
        <EmptyNote />
      ) : (
        <>
          <ol className="flex flex-col gap-4">
            {FUNNEL_STEPS.map((step, i) => {
              const count = funnel?.[step] || 0;
              const share = top ? count / top : 0;
              return (
                <li key={step} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm text-gray-700">{t(`dashboard.insights.funnel.steps.${step}`)}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatNumber(count, language)}{" "}
                      <span className="text-xs font-medium text-gray-500">{formatPercent(share, language)}</span>
                    </span>
                  </div>
                  <div className="h-7 overflow-hidden rounded-md bg-gray-100" aria-hidden="true">
                    <div
                      className={`h-full rounded-md ${i === FUNNEL_STEPS.length - 1 ? "bg-orange-600" : "bg-indigo-600"}`}
                      style={{ width: `${share * 100}%`, opacity: 1 - i * 0.15 }}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
          {drop && drop.lost > 0 && (
            <p className="mt-auto rounded-lg bg-orange-50 px-3 py-2.5 text-[13px] leading-relaxed text-gray-700">
              {t("dashboard.insights.funnel.biggestDrop", {
                from: t(`dashboard.insights.funnel.steps.${drop.from}`),
                to: t(`dashboard.insights.funnel.steps.${drop.to}`),
              })}
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

const STAFF_TONES = [
  "bg-indigo-50 text-indigo-800",
  "bg-orange-50 text-orange-800",
  "bg-emerald-50 text-emerald-800",
];

export function StaffTable({ staff, loading }) {
  const { t, language } = useLanguage();
  return (
    <Panel title={t("dashboard.insights.staff.title")}>
      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : !staff?.length ? (
        <EmptyNote />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left">
                <th scope="col" className="py-1.5 text-xs font-semibold text-gray-600">
                  {t("dashboard.insights.staff.name")}
                </th>
                <th scope="col" className="py-1.5 text-right text-xs font-semibold text-gray-600">
                  {t("dashboard.insights.staff.scans")}
                </th>
                <th scope="col" className="py-1.5 pl-3 text-right text-xs font-semibold text-gray-600">
                  {t("dashboard.insights.staff.rewards")}
                </th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s, i) => (
                <tr key={s.user_id ?? i} className="border-t border-gray-100">
                  <td className="py-2.5">
                    <span className="inline-flex items-center gap-2.5">
                      <span
                        aria-hidden="true"
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${STAFF_TONES[i % STAFF_TONES.length]}`}
                      >
                        {initialsFrom(s.name).charAt(0) || "?"}
                      </span>
                      {s.name}
                    </span>
                  </td>
                  <td className="py-2.5 text-right font-semibold text-gray-900">
                    {formatNumber(s.scans ?? 0, language)}
                  </td>
                  <td className="py-2.5 pl-3 text-right text-gray-700">
                    {formatNumber(s.rewards ?? 0, language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

export function CardsBreakdown({ cards, rangeDays, loading }) {
  const { t, language } = useLanguage();
  return (
    <Panel title={t("dashboard.insights.cards.title")}>
      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : !cards?.length ? (
        <EmptyNote />
      ) : (
        <>
          <ul className="flex flex-col gap-2.5">
            {cards.map((c) => (
              <li
                key={c.pass_id}
                className="flex items-center gap-3.5 rounded-[10px] border border-gray-100 p-3"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-semibold text-gray-900">{c.name}</span>
                  <span className="text-xs text-gray-500">
                    {t("dashboard.insights.cards.customers", {
                      count: c.customers ?? 0,
                      value: formatNumber(c.customers ?? 0, language),
                    })}
                  </span>
                </div>
                <span className="text-[15px] font-semibold text-gray-900">
                  {formatPercent(c.visit_share, language)}
                </span>
              </li>
            ))}
          </ul>
          <span className="text-xs text-gray-500">
            {t("dashboard.insights.cards.footnote", { count: rangeDays })}
          </span>
        </>
      )}
    </Panel>
  );
}

export function ComingUp({ upcoming, loading, canPush, onExpiryReminder }) {
  const { t, language } = useLanguage();
  const birthdays = upcoming?.birthdays_7d;
  const expiring = upcoming?.expiring_30d;

  return (
    <Panel title={t("dashboard.insights.upcoming.title")} className="flex-[1_1_300px]">
      {loading ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <>
          <div className="flex flex-col gap-2.5 border-b border-gray-100 pb-4">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("dashboard.insights.upcoming.birthdays")}
              </span>
              <span className="text-xl font-semibold text-gray-900">
                {formatNumber(birthdays?.count ?? 0, language)}
              </span>
            </div>
            {(birthdays?.customers || []).slice(0, 5).map((c) => (
              <div key={c.id} className="flex justify-between gap-2 text-[13px]">
                <Link
                  to={`/customers/${encodeURIComponent(c.uuid || c.id)}`}
                  className="min-w-0 truncate text-gray-900 hover:text-indigo-700"
                >
                  {c.name}
                </Link>
                <span className="shrink-0 text-gray-500">{formatBirthday(c.birthday, language)}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("dashboard.insights.upcoming.expiring")}
              </span>
              <span className="text-xl font-semibold text-gray-900">
                {formatNumber(expiring?.points ?? 0, language)}
              </span>
            </div>
            <span className="text-[13px] text-gray-500">
              {t("dashboard.insights.upcoming.expiringHeldBy", {
                count: expiring?.customers ?? 0,
                value: formatNumber(expiring?.customers ?? 0, language),
              })}
            </span>
            {canPush && (expiring?.customers ?? 0) > 0 && (
              <button
                type="button"
                onClick={onExpiryReminder}
                className="min-h-11 self-start rounded-[10px] border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-900 hover:border-gray-400"
              >
                {t("dashboard.insights.upcoming.sendReminder")}
              </button>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}
